/**
 * m3 echo Frontend - Dashboard Page (Refactored)
 *
 * Haupt-Dashboard mit Project-Übersicht und Statistiken.
 * Refactored to use React Query hooks and Zustand stores.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  TextField,
  Collapse,
  Grid,
  Checkbox,
  Alert,
  Snackbar,
  MenuItem,
  Card,
  CardActionArea,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import CreateProjectDialog from '../components/CreateProjectDialog';
import apiClient, { projectsApi } from '../services/api';

// New hooks and stores
import { useProjects, useRetestProject, useDeleteProject, projectKeys } from '../hooks/useProjects';
import { useUIStore } from '../stores/useUIStore';
import { useAuthStore, useCanEdit } from '../stores/useAuthStore';
import { useQueryClient } from '@tanstack/react-query';
import { tenantApi, TenantOption } from '../services/admin';
import { useAutopilots } from '../hooks/useAutopilots';
import { Project } from '../types/api';
import DashboardSkeleton from '../components/skeletons/DashboardSkeleton';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { billingApi } from '../services/billing';
import { useOptimizationQuota } from '../hooks/useOptimization';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const reduceMotion = useReducedMotion();

  // Get auth state
  const { user, setTenant } = useAuthStore();
  const isAdmin = user?.is_admin || false;
  const canEdit = useCanEdit();

  // Get UI state from Zustand store
  const {
    dashboardFilters,
    setSearchTermFilter,
    clearDashboardFilters,
    filtersExpanded,
    toggleFiltersExpanded,
    openCreateProjectDialog,
    deleteProjectDialogOpen,
    openDeleteProjectDialog,
    closeDeleteProjectDialog,
    confirmDeleteProject,
  } = useUIStore();

  // Billing: check if tenant has core_access module and optimization module
  const [hasCoreAccess, setHasCoreAccess] = useState(true); // default true to avoid flash
  // Manual LLM Score is gated by the `manual-llm-score` billing module
  // (feature_key=allow_manual_llm_score). Default to false so the button stays
  // hidden until we have confirmation — opposite of `hasCoreAccess` because
  // accidentally exposing the manual button is a cost risk, not a UX one.
  const [allowManualLlmScore, setAllowManualLlmScore] = useState(false);
  useEffect(() => {
    billingApi.getMyConfig().then((res) => {
      setHasCoreAccess(!!res.data.active_features['core_access']);
      setAllowManualLlmScore(!!res.data.active_features['allow_manual_llm_score']);
    }).catch(() => {
      // If billing endpoint not available, allow core_access (backward compat)
      // but keep manual LLM Score off until billing answers.
      setHasCoreAccess(true);
      setAllowManualLlmScore(false);
    });
  }, []);

  // Use React Query hook for optimization quota (auto-refreshes on invalidation)
  const { hasModule: hasOptimierungModule, runsUsed, runsRemaining, runQuota } = useOptimizationQuota();
  const optimizationQuota = { used: runsUsed, remaining: runsRemaining, total: runQuota };

  // Admin tenant selection
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const [tenantsLoading, setTenantsLoading] = useState(false);

  // Fetch tenants for admin users
  useEffect(() => {
    if (isAdmin) {
      loadTenants();
    }
  }, [isAdmin]);

  const loadTenants = async () => {
    try {
      setTenantsLoading(true);
      const response = await tenantApi.getOptions();
      setTenants(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Mandanten:', error);
    } finally {
      setTenantsLoading(false);
    }
  };

  // Use React Query hooks for data fetching
  const { data: projects, isLoading } = useProjects({
    search_term_filter: dashboardFilters.searchTerm || undefined,
    tenant_id: isAdmin ? selectedTenantId || undefined : undefined,
  });

  // Fetch all autopilots to show indicators. Through the key factory: the
  // literal `['autopilots']` this used to sit on is the same key for every
  // account and tenant, so an identity switch could paint the previous
  // tenant's badges onto these rows.
  const { data: autopilots } = useAutopilots();

  // Use mutations
  const retestMutation = useRetestProject();
  const deleteMutation = useDeleteProject();

  // Bulk selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<number[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  /**
   * Toggles selection mode
   */
  const handleToggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedProjects([]);
  };

  /**
   * Toggles project selection
   */
  const handleToggleProject = (projectId: number) => {
    setSelectedProjects((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    );
  };

  /**
   * Selects all projects
   */
  const handleSelectAll = () => {
    if (selectedProjects.length === projects?.length) {
      setSelectedProjects([]);
    } else {
      setSelectedProjects(projects?.map((p: Project) => p.id) || []);
    }
  };

  /**
   * Triggers LLM Score for selected projects
   */
  const handleBulkLLMScore = async () => {
    if (selectedProjects.length === 0) return;

    setBulkActionLoading(true);
    try {
      // Trigger retest for each selected project
      await Promise.all(selectedProjects.map((projectId) => projectsApi.retest(projectId)));

      // Invalidate React Query caches to refresh UI
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      selectedProjects.forEach((projectId) => {
        queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      });

      setSnackbarMessage(`${selectedProjects.length} LLM Score Tests erfolgreich gestartet`);
      setSnackbarOpen(true);
      setSelectedProjects([]);
      setSelectionMode(false);
    } catch (error) {
      console.error('Error triggering bulk LLM Score:', error);
      setSnackbarMessage('Fehler beim Starten der LLM Score Tests');
      setSnackbarOpen(true);
    } finally {
      setBulkActionLoading(false);
    }
  };

  /**
   * Triggers Benchmark for selected projects
   */
  const handleBulkBenchmark = async () => {
    if (selectedProjects.length === 0) return;

    setBulkActionLoading(true);
    try {
      // For each project, check if homepage is configured
      // Track which type of test each project gets
      const testTypes: ('benchmark' | 'llm')[] = [];
      const projectPromises = selectedProjects.map((projectId) => {
        const project = projects?.find((p: Project) => p.id === projectId);
        if (!project) {
          testTypes.push('benchmark');
          return Promise.reject(new Error('Project not found'));
        }

        if (project.homepage_url) {
          testTypes.push('benchmark');
          return apiClient.post(`/optimization/projects/${projectId}/tests`, {
            homepage_url: project.homepage_url,
          });
        } else {
          testTypes.push('llm');
          return projectsApi.retest(projectId);
        }
      });

      const results = await Promise.allSettled(projectPromises);

      // Count successes/failures by type
      let benchmarkOk = 0, llmOk = 0, failedCount = 0;
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          if (testTypes[i] === 'benchmark') benchmarkOk++;
          else llmOk++;
        } else {
          failedCount++;
        }
      });
      const succeeded = benchmarkOk + llmOk;

      // Invalidate React Query caches to refresh UI
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['billing', 'my-config'] });
      selectedProjects.forEach((projectId) => {
        queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      });

      // Build appropriate result message distinguishing test types
      let message = '';
      if (failedCount > 0 && succeeded > 0) {
        message = `${succeeded} von ${selectedProjects.length} Tests gestartet. ${failedCount} Projekte übersprungen (Cooldown/Kontingent).`;
      } else if (failedCount > 0 && succeeded === 0) {
        message = `Keine Tests gestartet. ${failedCount} Projekte übersprungen (Cooldown/Kontingent).`;
      } else if (llmOk > 0 && benchmarkOk > 0) {
        message = `${benchmarkOk} Optimierung Tests und ${llmOk} LLM Score Tests gestartet`;
      } else if (llmOk > 0) {
        message = `${llmOk} LLM Score Tests gestartet (keine Homepage-URL konfiguriert)`;
      } else {
        message = `${succeeded} Optimierung Tests erfolgreich gestartet`;
      }

      setSnackbarMessage(message);
      setSnackbarOpen(true);
      setSelectedProjects([]);
      setSelectionMode(false);
    } catch (error) {
      console.error('Error triggering bulk benchmark:', error);
      setSnackbarMessage('Fehler beim Starten der Tests');
      setSnackbarOpen(true);
    } finally {
      setBulkActionLoading(false);
    }
  };

  /**
   * Opens bulk delete confirmation dialog
   */
  const handleOpenBulkDelete = () => {
    if (selectedProjects.length === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  /**
   * Confirms and executes bulk delete
   */
  const handleConfirmBulkDelete = async () => {
    setBulkActionLoading(true);
    setBulkDeleteDialogOpen(false);

    try {
      // Delete all selected projects
      await Promise.all(selectedProjects.map((projectId) => projectsApi.delete(projectId)));

      // Invalidate React Query caches to refresh UI
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });

      setSnackbarMessage(`${selectedProjects.length} Projekt(e) erfolgreich gelöscht`);
      setSnackbarOpen(true);
      setSelectedProjects([]);
      setSelectionMode(false);
    } catch (error) {
      console.error('Error deleting projects:', error);
      setSnackbarMessage('Fehler beim Löschen der Projekte');
      setSnackbarOpen(true);
    } finally {
      setBulkActionLoading(false);
    }
  };

  /**
   * Formats date for display
   */
  const formatDate = (dateString: string, short = false): string => {
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDateString);
    if (short) {
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      });
    }
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Opens project details
   */
  const handleViewDetails = (projectId: number) => {
    navigate(`/projects/${projectId}`);
  };

  /**
   * Creates retest for a project
   */
  const handleRetest = (projectId: number) => {
    retestMutation.mutate(projectId);
  };

  /**
   * Handles delete confirmation
   */
  const handleDeleteConfirm = () => {
    const projectId = confirmDeleteProject();
    if (projectId) {
      deleteMutation.mutate(projectId);
    }
  };

  /**
   * Gets trend icon
   */
  // Trend = score difference between the two latest runs as the backend
  // reports it (any difference > 0 counts). Without a score on both sides
  // there is no comparison, so no trend is shown.
  const getTrendIcon = (trend: number | null) => {
    if (trend === null) return <Typography component="span" color="text.secondary">-</Typography>;
    if (trend > 0) return <TrendingUpIcon sx={{ color: 'success.main' }} />;
    if (trend < 0) return <TrendingDownIcon sx={{ color: 'error.main' }} />;
    return <TrendingFlatIcon sx={{ color: 'grey.500' }} />;
  };

  /**
   * Checks if project has active jobs (pending or running)
   */
  const hasActiveJobs = (lastJobStatus: string | null) => {
    return lastJobStatus === 'pending' || lastJobStatus === 'running';
  };

  /**
   * Checks if project has active autopilots
   */
  const hasActiveAutopilots = (projectId: number): boolean => {
    if (!autopilots) return false;
    return autopilots.some((ap) => ap.project_id === projectId && ap.is_active);
  };

  /**
   * Gets autopilot count for a project
   */
  const getAutopilotCount = (projectId: number): number => {
    if (!autopilots) return 0;
    return autopilots.filter((ap) => ap.project_id === projectId && ap.is_active).length;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: { xs: 1, sm: 0 }, mb: 3 }}>
        <Typography variant="h5" component="h1">
          Projekte
        </Typography>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          {canEdit && selectionMode && selectedProjects.length > 0 && (
            <>
              <Button
                variant="contained"
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                startIcon={<ReplayIcon />}
                onClick={handleBulkLLMScore}
                disabled={bulkActionLoading}
              >
                LLM Score
              </Button>
              {(isAdmin || hasOptimierungModule) && (
                <Tooltip title={optimizationQuota.remaining > 0 ? `Verfügbar (${optimizationQuota.remaining}/${optimizationQuota.total})` : `Kontingent aufgebraucht (${optimizationQuota.used}/${optimizationQuota.total})`} arrow>
                  <span>
                    <Button
                      variant="contained"
                      color="secondary"
                      size={isMobile ? 'small' : 'medium'}
                      startIcon={<AssessmentIcon />}
                      onClick={handleBulkBenchmark}
                      disabled={bulkActionLoading || (!isAdmin && optimizationQuota.remaining <= 0)}
                    >
                      Optimierung
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button
                variant="contained"
                color="error"
                size={isMobile ? 'small' : 'medium'}
                startIcon={<DeleteIcon />}
                onClick={handleOpenBulkDelete}
                disabled={bulkActionLoading}
              >
                Löschen
              </Button>
            </>
          )}
          {canEdit && (
            <Button
              variant={selectionMode ? 'contained' : 'outlined'}
              size={isMobile ? 'small' : 'medium'}
              startIcon={<CheckBoxIcon />}
              onClick={handleToggleSelectionMode}
            >
              Auswahl
            </Button>
          )}
          {!selectionMode && (
            <>
              <Button
                variant="outlined"
                size={isMobile ? 'small' : 'medium'}
                startIcon={<FilterListIcon />}
                onClick={toggleFiltersExpanded}
              >
                Filter
              </Button>
              {canEdit && hasCoreAccess && (
                <Button
                  variant="contained"
                  size={isMobile ? 'small' : 'medium'}
                  startIcon={<AddIcon />}
                  onClick={openCreateProjectDialog}
                >
                  Neues Projekt
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Admin Tenant Selector */}
      {isAdmin && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Mandant auswählen"
                value={selectedTenantId ?? 0}
                onChange={(e) => {
                  const tenantId = Number(e.target.value);
                  setSelectedTenantId(tenantId === 0 ? null : tenantId);
                  // Also update global auth store for CreateProjectDialog
                  const selectedTenant = tenants.find(t => t.id === tenantId);
                  if (selectedTenant) {
                    setTenant(selectedTenant);
                  }
                }}
                disabled={tenantsLoading}
                helperText="Wählen Sie einen Mandanten aus, um dessen Projekte anzuzeigen"
              >
                <MenuItem value={0}>
                  <em>Alle Mandanten ({tenants.reduce((sum, t) => sum + (t.project_count || 0), 0)} Projekte)</em>
                </MenuItem>
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.display_name} ({t.project_count} Projekte)
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Filter Section */}
      <Collapse in={filtersExpanded}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={10}>
              <TextField
                fullWidth
                size="small"
                label="Suchbegriff"
                value={dashboardFilters.searchTerm}
                onChange={(e) => setSearchTermFilter(e.target.value)}
                placeholder="z.B. Stadtwerke"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearDashboardFilters}
              >
                Filter löschen
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Projects Table / Mobile Cards */}
      {isLoading ? (
        <DashboardSkeleton />
      ) : isMobile ? (
        /* Mobile: Card-based layout */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {selectionMode && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                size="small"
                checked={selectedProjects.length === projects?.length && (projects?.length || 0) > 0}
                indeterminate={selectedProjects.length > 0 && selectedProjects.length < (projects?.length || 0)}
                onChange={handleSelectAll}
              />
              <Typography variant="body2" color="text.secondary">
                {selectedProjects.length > 0 ? `${selectedProjects.length} ausgewählt` : 'Alle auswählen'}
              </Typography>
            </Box>
          )}
          {projects && projects.length > 0 ? (
            projects.map((project: Project) => (
              <Card key={project.id} variant="outlined">
                <CardActionArea
                  onClick={() => selectionMode ? handleToggleProject(project.id) : handleViewDetails(project.id)}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {selectionMode && (
                      <Checkbox
                        size="small"
                        checked={selectedProjects.includes(project.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleToggleProject(project.id)}
                        sx={{ p: 0 }}
                      />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                          {project.name}
                        </Typography>
                        {hasActiveJobs(project.last_job_status) && (() => {
                          const pct = project.last_job_progress_total
                            ? ((project.last_job_progress_current || 0) / project.last_job_progress_total) * 100
                            : 0;
                          const deg = Math.round(pct * 3.6);
                          return (
                            <Tooltip title={project.last_job_progress_total
                              ? `${project.last_job_progress_current || 0}/${project.last_job_progress_total}`
                              : 'Jobs werden ausgeführt oder warten'}>
                              <Box sx={{
                                width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                                background: deg > 0
                                  ? `conic-gradient(#999 ${deg}deg, #ccc ${deg}deg 360deg)`
                                  : '#ccc',
                              }} />
                            </Tooltip>
                          );
                        })()}
                        {hasActiveAutopilots(project.id) && (
                          <AutorenewIcon sx={{ color: 'primary.main', fontSize: '1rem', ...(!reduceMotion && { animation: 'iconSpin 3s linear infinite', '@keyframes iconSpin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }) }} />
                        )}
                        {project.last_job_status === 'failed' && (
                          <Tooltip title="Letzter Test fehlgeschlagen">
                            <WarningAmberIcon sx={{ color: 'error.main', fontSize: '1rem' }} />
                          </Tooltip>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {project.search_term}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                        <Chip
                          label={`${project.total_jobs || 0} Tests`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {project.average_score != null ? project.average_score.toFixed(2) : '-'}
                        </Typography>
                        {getTrendIcon(project.score_trend ?? null)}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {project.last_job_date ? formatDate(project.last_job_date, true) : '-'}
                        </Typography>
                      </Box>
                    </Box>
                    {!selectionMode && (
                      <ChevronRightIcon sx={{ color: 'grey.400', flexShrink: 0 }} />
                    )}
                  </Box>
                </CardActionArea>
              </Card>
            ))
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              {dashboardFilters.searchTerm ? (
                <Typography color="text.secondary">
                  Keine Projekte mit diesem Suchbegriff gefunden
                </Typography>
              ) : canEdit ? (
                <>
                  <RocketLaunchOutlinedIcon
                    sx={{ fontSize: 56, color: 'primary.main', mb: 1 }}
                  />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Erstellen Sie Ihr erstes Projekt
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    Starten Sie mit einem Projekt, um die LLM-Sichtbarkeit Ihrer Marke zu messen.
                  </Typography>
                  {hasCoreAccess && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={openCreateProjectDialog}
                    >
                      Neues Projekt
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <InfoOutlinedIcon
                    sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
                  />
                  <Typography color="text.secondary">
                    Noch keine Projekte vorhanden. Bitten Sie Ihren Administrator, ein Projekt zu erstellen.
                  </Typography>
                </>
              )}
            </Paper>
          )}
        </Box>
      ) : (
        /* Desktop: Table layout */
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  {selectionMode && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedProjects.length === projects?.length && projects.length > 0}
                        indeterminate={
                          selectedProjects.length > 0 && selectedProjects.length < (projects?.length || 0)
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                  )}
                  <TableCell>ID</TableCell>
                  <TableCell>Projekt-Name</TableCell>
                  <TableCell>Suchbegriff</TableCell>
                  <TableCell>Tests</TableCell>
                  <TableCell>Durchschnitt</TableCell>
                  <TableCell>Letzter Test</TableCell>
                  <TableCell>Trend</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects && projects.length > 0 ? (
                  projects.map((project: Project) => (
                    <TableRow
                      key={project.id}
                      hover
                      sx={{ cursor: selectionMode ? 'default' : 'pointer' }}
                      onClick={() => !selectionMode && handleViewDetails(project.id)}
                    >
                      {selectionMode && (
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedProjects.includes(project.id)}
                            onChange={() => handleToggleProject(project.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>{project.id}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {project.name}
                            </Typography>
                            {project.description && (
                              <Typography variant="caption" color="text.secondary">
                                {project.description}
                              </Typography>
                            )}
                          </Box>
                          {hasActiveJobs(project.last_job_status) && (() => {
                            const pct = project.last_job_progress_total
                              ? ((project.last_job_progress_current || 0) / project.last_job_progress_total) * 100
                              : 0;
                            const deg = Math.round(pct * 3.6);
                            return (
                              <Tooltip title={project.last_job_progress_total
                                ? `${project.last_job_progress_current || 0}/${project.last_job_progress_total}`
                                : 'Jobs werden ausgeführt oder warten'}>
                                <Box sx={{
                                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                                  background: deg > 0
                                    ? `conic-gradient(#999 ${deg}deg, #ccc ${deg}deg 360deg)`
                                    : '#ccc',
                                }} />
                              </Tooltip>
                            );
                          })()}
                          {hasActiveAutopilots(project.id) && (
                            <Tooltip title={`${getAutopilotCount(project.id)} aktive${getAutopilotCount(project.id) > 1 ? '' : 'r'} Autopilot${getAutopilotCount(project.id) > 1 ? 's' : ''}`}>
                              <AutorenewIcon sx={{ color: 'primary.main', fontSize: '1.2rem', ...(!reduceMotion && { animation: 'iconSpin 3s linear infinite', '@keyframes iconSpin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }) }} />
                            </Tooltip>
                          )}
                          {project.last_job_status === 'failed' && (
                            <Tooltip title="Letzter Test fehlgeschlagen">
                              <WarningAmberIcon sx={{ color: 'error.main', fontSize: '1.2rem' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {project.search_term}
                          {(project.search_term_synonym1 || project.search_term_synonym2) && (
                            <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              ({[project.search_term_synonym1, project.search_term_synonym2].filter(Boolean).join(', ')})
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{project.total_jobs || 0}</TableCell>
                      <TableCell>
                        {project.average_score != null ? project.average_score.toFixed(2) : '-'}
                      </TableCell>
                      <TableCell>
                        {project.last_job_date ? formatDate(project.last_job_date) : '-'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={project.score_trend != null ? `Änderung: ${project.score_trend.toFixed(2)}` : 'Kein Vergleichswert'}>
                          <Box>{getTrendIcon(project.score_trend ?? null)}</Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Details anzeigen">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(project.id)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {canEdit && (isAdmin || allowManualLlmScore) && (
                          <Tooltip title="Re-Test erstellen">
                            <IconButton
                              size="small"
                              onClick={() => handleRetest(project.id)}
                              disabled={retestMutation.isPending}
                            >
                              <ReplayIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canEdit && (
                          <Tooltip title="Projekt löschen">
                            <IconButton
                              size="small"
                              onClick={() => openDeleteProjectDialog(project.id)}
                              color="error"
                              disabled={deleteMutation.isPending}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={selectionMode ? 9 : 8} align="center" sx={{ py: 5 }}>
                      {dashboardFilters.searchTerm ? (
                        <Typography color="text.secondary">
                          Keine Projekte mit diesem Suchbegriff gefunden
                        </Typography>
                      ) : canEdit ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <RocketLaunchOutlinedIcon
                            sx={{ fontSize: 56, color: 'primary.main', mb: 1 }}
                          />
                          <Typography variant="h6">
                            Erstellen Sie Ihr erstes Projekt
                          </Typography>
                          <Typography color="text.secondary" sx={{ mb: 1 }}>
                            Starten Sie mit einem Projekt, um die LLM-Sichtbarkeit Ihrer Marke zu messen.
                          </Typography>
                          {hasCoreAccess && (
                            <Button
                              variant="contained"
                              startIcon={<AddIcon />}
                              onClick={openCreateProjectDialog}
                            >
                              Neues Projekt
                            </Button>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <InfoOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                          <Typography color="text.secondary">
                            Noch keine Projekte vorhanden. Bitten Sie Ihren Administrator, ein Projekt zu erstellen.
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Create Project Dialog */}
      <CreateProjectDialog />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteProjectDialogOpen} onClose={closeDeleteProjectDialog}>
        <DialogTitle>Projekt löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie dieses Projekt wirklich löschen? Diese Aktion kann nicht rückgängig gemacht
            werden. Alle zugehörigen Jobs und Ergebnisse werden ebenfalls gelöscht.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteProjectDialog}>Abbrechen</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Lösche...' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)}>
        <DialogTitle>Projekte löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie {selectedProjects.length} Projekt(e) löschen wollen?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'warning.main' }}>
            Diese Aktion kann nicht rückgängig gemacht werden. Alle zugehörigen Jobs und Ergebnisse
            werden ebenfalls gelöscht.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleConfirmBulkDelete}
            color="error"
            variant="contained"
            disabled={bulkActionLoading}
          >
            {bulkActionLoading ? 'Lösche...' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
